import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Layers } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { Button } from "../ui/button";
import { toast } from "sonner";

import { trackEvent, toGaItem } from "../../lib/analyticsEvents";

// ── Mapping unique catégorie slug → label affiché ─────────────────
const CATEGORY_LABELS = {
	viennoiserie: "Viennoiserie",
	sandwich: "Sandwich",
	snacks: "Snack",
	cafes: "Café",
	"boissons-froides": "Boisson froide",
	dessert: "Dessert",
};

export const ProductCard = ({ product, index = 0 }) => {
	const { addItem } = useCart();
	const navigate = useNavigate();

	const hasVariants = product.has_variants === true;
	const categorySlug = product.category_slug || product.category;
	const categoryLabel = CATEGORY_LABELS[categorySlug] || categorySlug || "";

	const formatPrice = (price) =>
		new Intl.NumberFormat("fr-CH", {
			style: "currency",
			currency: "CHF",
		}).format(price);

	// Le "+" dans le coin
	//   - Produit simple → ajoute direct au panier
	//   - Produit à variantes → redirige vers la fiche pour choisir
	const handleQuickAction = (e) => {
		e.preventDefault();
		e.stopPropagation();

		if (hasVariants) {
			navigate(`/produit/${product.slug}`);
			return;
		}

		addItem(product, 1);

		trackEvent("add_to_cart", {
			currency: "CHF",
			value: Number(product.price || 0),
			items: [toGaItem(product, 1)],
		});

		toast.success(`${product.name} ajouté au panier`, {
			description: formatPrice(product.price),
			duration: 2000,
		});
	};

	const handleSelectItem = () => {
		trackEvent("select_item", {
			item_list_id: "menu",
			item_list_name: "Menu",
			items: [toGaItem(product, 1)],
		});
	};

	const animationDelay = (index % 6) * 0.1;

	// Le nombre de choix (variantes disponibles), si connu
	const variantsCount = Array.isArray(product.variants)
		? product.variants.length
		: 0;

	return (
		<article
			data-testid={`product-card-${product.id}`}
			className="card-product opacity-0 animate-fade-in"
			style={{ animationDelay: `${animationDelay}s` }}
		>
			<Link
				to={`/produit/${product.slug}`}
				className="block"
				onClick={handleSelectItem}
			>
				{/* Image */}
				<div className="relative w-full aspect-square overflow-hidden mb-4 flex items-center justify-center">
					{product.image_url ? (
						<img
							src={product.image_url}
							alt={product.name}
							loading="lazy"
							className="w-full h-full object-contain"
						/>
					) : (
						<div className="w-full h-full flex items-center justify-center text-soft-charcoal-secondary/40 text-xs uppercase tracking-widest">
							{product.name}
						</div>
					)}

					{/* Badges haut-gauche */}
					{product.featured && (
						<span className="absolute top-4 left-4 bg-latte-gold text-warm-alabaster px-3 py-1 text-xs font-medium uppercase tracking-wider">
							Vedette
						</span>
					)}

					{product.tags?.includes("nouveau") && !product.featured && (
						<span className="absolute top-4 left-4 bg-espresso text-warm-alabaster px-3 py-1 text-xs font-medium uppercase tracking-wider">
							Nouveau
						</span>
					)}

					{/* Badge variantes */}
					{hasVariants && variantsCount > 0 && (
						<span className="absolute top-4 right-4 bg-warm-alabaster text-espresso px-2.5 py-1 text-xs font-medium flex items-center gap-1.5 shadow-sm">
							<Layers className="w-3 h-3" />
							{variantsCount} choix
						</span>
					)}

					{/* Bouton ajout rapide */}
					<Button
						onClick={handleQuickAction}
						data-testid={`add-to-cart-${product.id}`}
						aria-label={
							hasVariants
								? `Voir les options pour ${product.name}`
								: `Ajouter ${product.name} au panier`
						}
						className="absolute bottom-4 right-4 w-10 h-10 p-0 bg-warm-alabaster text-espresso hover:bg-espresso hover:text-warm-alabaster opacity-0 group-hover:opacity-100 transition-all duration-300 rounded-full shadow-lg"
					>
						<Plus className="w-5 h-5" />
					</Button>
				</div>

				{/* Contenu */}
				<div className="space-y-2">
					{/* Catégorie */}
					<span className="text-xs uppercase tracking-widest text-latte-gold font-medium">
						{categoryLabel}
					</span>

					{/* Nom */}
					<h3 className="font-serif text-lg font-semibold text-espresso group-hover:text-latte-gold transition-colors duration-300">
						{product.name}
					</h3>

					{/* Description courte */}
					<p className="text-soft-charcoal-secondary text-sm line-clamp-2">
						{product.short_description}
					</p>

					{/* Prix + statut */}
					<div className="flex items-center justify-between pt-2">
						<span className="font-serif text-lg font-semibold text-espresso">
							{hasVariants && (
								<span className="text-xs text-soft-charcoal-secondary uppercase tracking-wider font-sans font-normal mr-1">
									dès
								</span>
							)}
							{formatPrice(product.price)}
						</span>

						{!product.available && (
							<span className="text-xs text-red-500 font-medium uppercase">
								Épuisé
							</span>
						)}
					</div>
				</div>
			</Link>
		</article>
	);
};

export default ProductCard;
